import {Graphics, Rectangle} from "pixi.js";
import {DrawFrmThing} from "../drawfrm/DrawFrmThing";
import {FrmRefPoint} from "../drawfrm/FrmRefPoint";
import {lineBlueGrey, lineBlueLight} from "../const";
import {drawSvgPath} from "./SvgPath";

// a borderless, icon-only clickable button for 2d scenes. The icon is supplied
// as SVG path `d` data (rendered crisp via drawSvgPath — see NavButton for the
// text-label sibling). Idle is just the tinted icon; hover paints a faint blue
// background behind it. Click fires onClick on a down+up with no drag, so
// panning the scene won't trigger it.
export type IconButtonOptions = {
    // SVG path `d` string, or several stacked into one icon (filled with color).
    path: string | string[];
    // fired on click.
    onClick: () => void;
    // the square viewBox the path is authored in. defaults to 24 (Material icons).
    viewBox?: number;
    // overall button size in px (square). defaults to DEFAULT_SIZE.
    size?: number;
    // inset between the icon and the button edge. defaults to DEFAULT_PADDING.
    padding?: number;
    // icon fill color. defaults to lineBlueLight.
    color?: number;
};

const DEFAULT_SIZE = 34;
const DEFAULT_PADDING = 6;
const CORNER_RADIUS = 4;

export class IconButton extends DrawFrmThing {

    // top-left corner of the button, so callers can link/position it.
    anchor: FrmRefPoint;
    // square footprint in px — callers use it to lay buttons out in a row.
    readonly size: number;

    private icon: Graphics;
    private hovered = false;

    constructor(opt: IconButtonOptions) {
        super();

        this.size = opt.size ?? DEFAULT_SIZE;
        const padding = opt.padding ?? DEFAULT_PADDING;
        const color = opt.color ?? lineBlueLight;
        const viewBox = opt.viewBox ?? 24;
        const paths = Array.isArray(opt.path) ? opt.path : [opt.path];

        // icon sits in its own Graphics on top of the hover background (this.lines).
        const scale = (this.size - padding * 2) / viewBox;
        this.icon = new Graphics();
        this.icon.position.set(padding, padding);
        this.icon.beginFill(color);
        for (const d of paths) drawSvgPath(this.icon, d, {scaleX: scale, scaleY: scale});
        this.icon.endFill();

        // draw the hover background first so the icon renders on top.
        this.draw();
        this.graphic.addChild(this.icon);

        this.anchor = this.makeRef("iconButton", 0, 0);

        this.makeInteractive({
            hitArea: () => new Rectangle(0, 0, this.size, this.size),
            onHover: (hovered) => {
                this.hovered = hovered;
                this.draw();
            },
            onClick: () => opt.onClick(),
        });
    }

    private draw() {
        const g = this.lines;
        g.clear();

        // always stroke the full (transparent) rect so the graphic's bounds stay
        // the square button size for layout + hit-testing; fill only on hover.
        g.lineStyle(0);
        g.beginFill(lineBlueGrey, this.hovered ? 0.6 : 0);
        g.drawRoundedRect(0, 0, this.size, this.size, CORNER_RADIUS);
        g.endFill();
    }
}
